import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { Building2 } from 'lucide-react';

export function CustomerTicketsTable({ customers = [], title = 'Tickets by Company' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const customerData = customers && Array.isArray(customers) ? customers : [];
  const maxTickets = Math.max(...customerData.map(c => c.count || 0));

  return (
    <div 
      className={cn(
        "relative rounded-3xl overflow-hidden transition-all duration-700 ease-out transform",
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
    >
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-white dark:bg-slate-800 z-0"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-blue-50/50 dark:from-indigo-900/20 dark:via-slate-800/50 dark:to-blue-900/20 z-0 pointer-events-none"></div>

      {/* Desktop: p-8, row. Mobile: p-5, stacked header */}
      <div className="relative z-10 p-5 md:p-8 border border-gray-100 dark:border-slate-700/50 rounded-3xl shadow-2xl shadow-gray-200/50 dark:shadow-black/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-xl shrink-0">
                    <Building2 className="w-5 h-5" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white tracking-tight">{title}</h3>
            </div>
            {customerData.length > 0 && (
                <span className="self-start sm:self-auto text-xs font-semibold text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 rounded-full whitespace-nowrap">
                    Top {customerData.length} Companies
                </span>
            )}
        </div>

        {!customerData.length ? (
          <div className="h-48 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 gap-3 border-2 border-dashed border-gray-100 dark:border-slate-700 rounded-2xl bg-gray-50/50 dark:bg-slate-800/50">
            <Building2 className="w-8 h-8 opacity-50" />
            <span className="text-sm font-medium">No company data available yet</span>
          </div>
        ) : (
          <div className="space-y-5">
            {customerData.map((company, index) => {
              const percentage = maxTickets > 0 ? (company.count / maxTickets) * 100 : 0;
              
              // Formatting Logic (Kept same as original for consistency)
              const formatCompanyName = (name) => {
                if (!name || name === 'Unknown Company') return 'Unknown Company';
                const suffixes = ['tech', 'infra', 'solutions', 'systems', 'services', 'corp', 'inc', 'ltd', 'group'];
                let formatted = name.toLowerCase();
                for (const suffix of suffixes) {
                  if (formatted.endsWith(suffix) && formatted.length > suffix.length) {
                    const base = formatted.slice(0, -suffix.length);
                    formatted = `${base} ${suffix}`;
                    break;
                  }
                }
                formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');
                return formatted.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
              };
              
              const displayName = formatCompanyName(company.company_name);

              return (
                <div 
                  key={company.company_name || index} 
                  className="group relative"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-center justify-between text-sm mb-2 px-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {displayName}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded-md min-w-[2rem] text-center text-xs">
                        {company.count}
                    </span>
                  </div>
                  
                  {/* Progress Track */}
                  <div className="h-3 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                    {/* Animated Progress Bar */}
                    <div 
                      className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] relative overflow-hidden transition-all duration-1000 ease-out group-hover:from-indigo-400 group-hover:to-blue-400"
                      style={{ width: `${percentage}%` }}
                    >
                        {/* Shimmer overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
